import { formatters } from '@Components/hierarchical-report/Layout'

import { AUTO_INTERVAL } from './chartIntervals'
import { axisAcceptsAnyGroupby, getXAxisFields, isAxisFieldAllowed } from './chartUtils'
import { getCollectionGroupbyOptions, getGroupbyFieldLabel } from './collectionGroupby'
import { getGroupedCollectionMetrics, getMetricDisplayLabel } from './collectionMetrics'
import { DEFAULT_COLLECTION, createDefaultReportConfigForCollection } from './defaultReports'

export { COLLECTIONS } from './collectionMetrics'

export const CHART_TYPES = [
  { value: 'line-chart', label: 'Line chart' },
  { value: 'bar-chart', label: 'Stacked bar chart' },
  { value: 'pie-chart', label: 'Pie chart' },
  { value: 'funnel-chart', label: 'Funnel chart' },
]

export const CHART_SIZE_OPTIONS = [
  { value: 'full', label: 'Full' },
  { value: 'half', label: 'Half' },
]

export function getChartColSpanClass(layoutSize = 'full') {
  return layoutSize === 'half' ? 'col-span-12 lg:col-span-6' : 'col-span-12'
}

export const METRIC_TYPES = [
  { value: 'count', label: 'Count' },
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Avg' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
  { value: 'cardinality', label: 'Cardinality' },
  { value: 'value_count', label: 'Value Count' },
  { value: 'stats', label: 'Stats' },
  { value: 'extended_stats', label: 'Extended Stats' },
  { value: 'percentiles', label: 'Percentiles' },
]

export function getAvailableMetricTypes(definition) {
  if (definition?.derived) return [{ value: 'derived', label: 'Derived' }]
  return METRIC_TYPES
}

export function applyMetricType(metric, type, definition) {
  if (definition?.derived || metric?.derived || type === 'derived') {
    return { key: metric.key, derived: true }
  }

  if (type === 'count') {
    return { key: metric.key, type: 'count' }
  }

  const nextMetric = { key: metric.key, type }
  if (definition?.field) nextMetric.field = definition.field
  if (type === 'percentiles' && definition?.options) {
    nextMetric.options = definition.options
  }

  return nextMetric
}

export const SORTBY_OPTIONS = [
  { value: 'total desc', label: 'Total desc', group: 'Total' },
  { value: 'total asc', label: 'Total asc', group: 'Total' },
  { value: 'uniqUser desc', label: 'Uniq User desc', group: 'Unique Users' },
  { value: 'uniqUser asc', label: 'Uniq User asc', group: 'Unique Users' },
  { value: 'revenue desc', label: 'Revenue desc', group: 'Revenue' },
  { value: 'revenue asc', label: 'Revenue asc', group: 'Revenue' },
]

export const SORT_DIRECTIONS = [
  { value: 'asc', label: 'Ascending' },
  { value: 'desc', label: 'Descending' },
]

export function parseSortbyEntries(sortby = []) {
  return sortby.map(entry => {
    const lastSpace = entry.lastIndexOf(' ')
    if (lastSpace === -1) return { field: entry, direction: 'desc' }

    return {
      field: entry.slice(0, lastSpace),
      direction: entry.slice(lastSpace + 1) || 'desc',
    }
  })
}

export function formatSortbyEntries(entries = []) {
  return entries
    .filter(entry => entry.field)
    .map(({ field, direction }) => `${field} ${direction || 'desc'}`)
}

export function getSortFieldOptions(groupby = [], metrics = [], collection) {
  const options = []
  const seen = new Set()

  const addOption = (value, label, group) => {
    if (!value || seen.has(value)) return
    seen.add(value)
    options.push({ value, label, group })
  }

  groupby.forEach(field => {
    addOption(field, getGroupbyFieldLabel(collection, field), 'Group by')
  })

  metrics.forEach(metric => {
    if (metric?.key) {
      addOption(
        metric.key,
        getMetricDisplayLabel(collection, metric),
        'Metrics',
      )
    }
  })

  SORTBY_OPTIONS.forEach(option => {
    const field = option.value.split(' ')[0]
    addOption(field, formatters.groupsText(field), option.group)
  })

  return options
}

const FILTER_GROUP_ORDER = {
  Time: 1,
  Traffic: 2,
  Entity: 3,
  Total: 1,
  'Unique Users': 2,
  Revenue: 3,
  Other: 99,
}

export function getGroupedFilterOptions(options = []) {
  const grouped = {}

  options.forEach(option => {
    const group = option.group || 'Other'
    grouped[group] = grouped[group] || []
    grouped[group].push(option)
  })

  return Object.keys(grouped)
    .sort((a, b) => (FILTER_GROUP_ORDER[a] || 99) - (FILTER_GROUP_ORDER[b] || 99))
    .map(group => [group, grouped[group]])
}

export const createDefaultReportConfig = dates => createDefaultReportConfigForCollection(DEFAULT_COLLECTION, dates)

export const createDefaultChart = (config = {}) => {
  const metricKeys = config.metrics?.length
    ? config.metrics.map(metric => metric.key).filter(Boolean)
    : ['total']

  return {
    name: '',
    dimensions: getDefaultChartDimensions(config.groupby, 'line-chart'),
    metricKeys,
    seriesBy: null,
    breakdown: metricKeys[0] || 'total',
    dataType: 'line-chart',
    layoutSize: 'full',
    interval: AUTO_INTERVAL,
  }
}

export function getDefaultChartDimensions(groupby = [], dataType) {
  const [timeField] = getXAxisFields(groupby)
  if (timeField) return [timeField]
  if (axisAcceptsAnyGroupby(dataType)) return groupby.slice(0, 1)

  return []
}

export function getChartDimensionOptions(collection, dataType) {
  return getCollectionGroupbyOptions(collection)
    .filter(option => isAxisFieldAllowed(option.value, dataType))
    .map(option => ({
      value: option.value,
      label: option.label,
      group: option.group,
    }))
}

export function getChartMetricOptions(collection) {
  return getGroupedCollectionMetrics(collection).flatMap(([group, metrics]) => metrics.map(metric => ({
    value: metric.key,
    label: metric.label,
    group,
  })))
}

export function getDefaultChartName(config = {}, metricKeys = []) {
  const keys = metricKeys.length
    ? metricKeys
    : config.metrics?.map(metric => metric.key).filter(Boolean) || ['total']

  const labels = keys
    .map(key => getMetricDisplayLabel(config.collection, { key }) || key)
    .filter(Boolean)

  return labels.join(', ') || 'Chart'
}

export const DATA_TYPE_LABELS = {
  table: 'Table',
  'line-chart': 'Line Chart',
  'bar-chart': 'Bar Chart',
  'pie-chart': 'Pie Chart',
  'funnel-chart': 'Funnel Chart',
}

export function isChartDataType(dataType) {
  return CHART_TYPES.some(type => type.value === dataType)
}

export function normalizeMetrics(metrics = []) {
  return metrics
    .filter(metric => metric?.key)
    .map(metric => {
      const { key, type, field, options, derived } = metric

      if (derived) return { key, derived: true }
      if (type === 'count') return { key, type: 'count' }

      const normalized = { key, type: type || 'sum' }
      if (field) normalized.field = field
      if (options) normalized.options = options
      return normalized
    })
}
